/* ===== Chat Page ===== */

import { useEffect, useState } from 'react'
import type { Channel, Message } from '@zetyar/domain'
import { Hash, Lock, MessageSquare, Plus, Radio, Send, Trash2 } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Badge } from '@/presentation/components/ui/Badge'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { PAGINATION } from '@/config'
import { webChatRepository } from '@/infrastructure/repositories/chat'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { useReauthGuard } from '@/presentation/hooks/useReauthGuard'

const CHANNEL_TYPE_ICON = {
  general: Radio,
  public: Hash,
  private: Lock,
} as const

export function ChatPage() {
  const { user, profile, currentMembership, currentOrganization, permissions } = useApp()
  const canCreateChannel = profile?.isSuperAdmin || permissions.includes('channels.create')
  const canModerate = profile?.isSuperAdmin || permissions.includes('channels.moderate')
  const { guard, reauthModal } = useReauthGuard()

  const [channels, setChannels] = useState<Channel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [draft, setDraft] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [channelName, setChannelName] = useState('')
  const [channelDescription, setChannelDescription] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)

  const tenantContext = () => {
    if (!currentOrganization || !profile || !user) return null
    return createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })
  }

  const loadChannels = async () => {
    const context = tenantContext()
    if (!context) {
      setChannels([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const result = await webChatRepository.listChannels(context, { limit: PAGINATION.chatMessageLimit })
      setChannels(result)
      setSelectedChannelId((current) => current ?? result[0]?.id ?? null)
    } catch {
      setChannels([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true
    const run = async () => {
      if (!isMounted) return
      await loadChannels()
    }
    void run()
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization, profile, user])

  const loadMessages = async (channelId: string, options?: { silent?: boolean }) => {
    const context = tenantContext()
    if (!context) return

    if (!options?.silent) setIsLoadingMessages(true)
    try {
      const result = await webChatRepository.listMessages(context, { channelId, limit: 100 })
      setMessages([...result].reverse())
    } catch (error) {
      console.error('Error al cargar los mensajes:', error)
      if (!options?.silent) setMessages([])
    } finally {
      if (!options?.silent) setIsLoadingMessages(false)
    }
  }

  useEffect(() => {
    if (!selectedChannelId) {
      setMessages([])
      return
    }

    void loadMessages(selectedChannelId)

    const context = tenantContext()
    if (context && webChatRepository.markChannelRead) {
      void webChatRepository.markChannelRead(context, selectedChannelId).then(() => {
        window.dispatchEvent(new CustomEvent('chat-read'))
      })
    }

    const intervalId = setInterval(() => {
      void loadMessages(selectedChannelId, { silent: true })
    }, 5_000)

    return () => clearInterval(intervalId)
    // currentMembership is intentionally a dependency (not just selectedChannelId):
    // it can finish loading after the channel auto-selects, and without this the
    // mark-as-read call above silently no-ops (missing membershipId) and never
    // retries, leaving the unread indicator stuck on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelId, currentMembership])

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    const context = tenantContext()
    if (!context || !selectedChannelId || !draft.trim()) return

    setIsSending(true)
    try {
      await webChatRepository.sendMessage(context, { channelId: selectedChannelId, content: draft.trim() })
      setDraft('')
      await loadMessages(selectedChannelId)
      if (webChatRepository.markChannelRead) {
        void webChatRepository.markChannelRead(context, selectedChannelId).then(() => {
          window.dispatchEvent(new CustomEvent('chat-read'))
        })
      }
    } catch (error) {
      console.error('Error al enviar el mensaje:', error)
    } finally {
      setIsSending(false)
    }
  }

  const handleCloseCreate = () => {
    setIsCreateOpen(false)
    setChannelName('')
    setChannelDescription('')
    setCreateError(null)
  }

  const handleCreateChannel = async (e: React.FormEvent) => {
    e.preventDefault()
    const context = tenantContext()
    if (!context || !webChatRepository.createChannel) return

    setCreateError(null)
    try {
      const channel = await webChatRepository.createChannel(context, {
        name: channelName,
        description: channelDescription || undefined,
      })
      await loadChannels()
      setSelectedChannelId(channel.id)
      handleCloseCreate()
    } catch (error: any) {
      console.error('Error al crear el canal:', error)
      let message = 'No tienes permisos para crear canales en esta organización.'
      if (error?.message) {
        if (error.message.includes('Role does not allow creating channels')) {
          message = 'Tu rol no tiene permisos para crear canales.'
        } else {
          message = error.message
        }
      }
      setCreateError(message)
    }
  }

  const selectedChannel = channels.find((channel) => channel.id === selectedChannelId) ?? null

  const handleDeleteChannel = (channelId: string) => {
    guard(async () => {
      const context = tenantContext()
      if (!context || !webChatRepository.deleteChannel) return

      try {
        await webChatRepository.deleteChannel(context, channelId)
        setSelectedChannelId((current) => (current === channelId ? null : current))
        await loadChannels()
      } catch (error) {
        console.error('Error al eliminar el canal:', error)
      }
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Chat</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Conversaciones internas de {currentOrganization?.name}
          </p>
        </div>
        {canCreateChannel && (
          <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setIsCreateOpen(true)}>
            Nuevo canal
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="w-5 h-5 text-primary-600" />
              Canales
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 py-4">Cargando canales...</p>
            ) : channels.length > 0 ? (
              <ul className="divide-y divide-neutral-200/70 dark:divide-neutral-800">
                {channels.map((channel) => {
                  const Icon = CHANNEL_TYPE_ICON[channel.type] ?? Hash
                  const isSelected = channel.id === selectedChannelId
                  return (
                    <li key={channel.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedChannelId(channel.id)}
                        className={`w-full flex items-center gap-3 py-3 text-left rounded-md px-2 transition-colors ${
                          isSelected
                            ? 'bg-primary-50 dark:bg-primary-950/50'
                            : 'hover:bg-neutral-50 dark:hover:bg-neutral-900'
                        }`}
                      >
                        <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0 bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-200">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                            {channel.name}
                          </p>
                          {channel.description && (
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                              {channel.description}
                            </p>
                          )}
                        </div>
                        <Badge variant="primary">{channel.type}</Badge>
                      </button>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 italic py-4">
                No hay canales todavia. Crea el primero para empezar a conversar.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 flex flex-col">
          <CardHeader>
            <CardTitle className="text-base">{selectedChannel ? selectedChannel.name : 'Selecciona un canal'}</CardTitle>
            {selectedChannel && canModerate && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 w-8 p-0 flex items-center justify-center"
                aria-label="Eliminar canal"
                onClick={() => handleDeleteChannel(selectedChannel.id)}
              >
                <Trash2 className="w-3.5 h-3.5 text-danger-600" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {!selectedChannel ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 py-4">
                Elige un canal de la lista o crea uno nuevo para empezar.
              </p>
            ) : (
              <>
                <div className="min-h-[16rem] max-h-[24rem] overflow-y-auto space-y-3 pr-1">
                  {isLoadingMessages ? (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Cargando mensajes...</p>
                  ) : messages.length === 0 ? (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      No hay mensajes todavia. Se el primero en escribir.
                    </p>
                  ) : (
                    messages.map((message) => (
                      <div key={message.id} className="flex flex-col gap-0.5">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                            {message.sender?.displayName ?? 'Miembro'}
                          </span>
                          <span className="text-xs text-neutral-400 dark:text-neutral-500">
                            {new Date(message.createdAt).toLocaleTimeString('es-CL', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-neutral-700 dark:text-neutral-300">{message.content}</p>
                      </div>
                    ))
                  )}
                </div>

                <form onSubmit={handleSend} className="flex items-center gap-2 pt-3 border-t border-neutral-100 dark:border-neutral-800">
                  <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Escribe un mensaje..."
                    className="flex-1 rounded-md border border-neutral-200 dark:border-neutral-800 bg-transparent px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <Button type="submit" variant="primary" disabled={isSending || !draft.trim()} leftIcon={<Send className="w-4 h-4" />}>
                    Enviar
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Modal isOpen={isCreateOpen} onClose={handleCloseCreate} title="Nuevo canal">
        <form onSubmit={handleCreateChannel} className="space-y-4">
          {createError && (
            <div className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:border-danger-900/70 dark:bg-danger-950/40 dark:text-danger-200 animate-fade-in">
              {createError}
            </div>
          )}
          <Input
            label="Nombre del canal"
            placeholder="Ej: general, directiva, eventos"
            required
            value={channelName}
            onChange={(e) => setChannelName(e.target.value)}
          />
          <Input
            label="Descripcion (opcional)"
            placeholder="Ej: Coordinacion de la directiva"
            value={channelDescription}
            onChange={(e) => setChannelDescription(e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={handleCloseCreate}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Crear canal
            </Button>
          </div>
        </form>
      </Modal>
      {reauthModal}
    </div>
  )
}
